/**
 * JSON Forms Editor - Test Utilities
 *
 * This file provides helper functions for testing the JSON Forms Editor.
 * Use the browser console to invoke these test functions.
 *
 * Example usage in browser console:
 *   window.testJsonEditor.openMOSFETEditor()
 *   window.testJsonEditor.openResistorEditor()
 */

const testJsonEditor = {
  /**
   * Test: Open MOSFET model parameters editor with nested structure (matches your example)
   */
  openMOSFETEditor() {
    if (typeof window.openJsonEditor !== 'function') {
      console.error('JSON Editor not available. Make sure LabLayout is mounted.');
      return;
    }

    window.openJsonEditor({
      schema: {
        type: 'object',
        title: 'Level-1 MOSFET Model',
        properties: {
          modelName: {
            type: 'string',
            title: 'Model name',
            pattern: '^[a-zA-Z][a-zA-Z0-9_]*$',
          },
          type: {
            type: 'string',
            title: 'Type',
            enum: ['NMOS', 'PMOS'],
          },
          level: {
            type: 'integer',
            title: 'Level',
            const: 1,
          },
          VTO: {
            type: 'number',
            title: 'Threshold voltage (V)',
            description: 'Zero-bias threshold voltage',
            minimum: -5,
            maximum: 5,
            default: 0.7,
          },
          KP: {
            type: 'number',
            title: 'KP (transconductance)',
            description: 'Transconductance parameter',
            minimum: 1e-6,
            default: 50e-6,
          },
          LAMBDA: {
            type: 'number',
            title: 'Lambda (1/V)',
            description: 'Channel-length modulation',
            minimum: 0,
            maximum: 1,
            default: 0.02,
          },
          GAMMA: {
            type: 'number',
            title: 'Gamma (body effect)',
            description: 'Body-effect parameter',
            minimum: 0,
            default: 0.5,
          },
          PHI: {
            type: 'number',
            title: 'Phi (surface potential)',
            description: 'Surface potential',
            minimum: 0,
            default: 0.8,
          },
        },
        required: ['modelName', 'type', 'VTO'],
      },
      uiSchema: {
        type: 'VerticalLayout',
        elements: [
          { type: 'Control', scope: '#/properties/modelName' },
          { type: 'Control', scope: '#/properties/type' },
          { type: 'Control', scope: '#/properties/level' },
          {
            type: 'Group',
            label: 'Parameters',
            elements: [
              { type: 'Control', scope: '#/properties/VTO' },
              { type: 'Control', scope: '#/properties/KP' },
              { type: 'Control', scope: '#/properties/LAMBDA' },
              { type: 'Control', scope: '#/properties/GAMMA' },
              { type: 'Control', scope: '#/properties/PHI' },
            ],
          },
        ],
      },
      data: {
        modelName: 'nmos1',
        type: 'NMOS',
        level: 1,
        VTO: 0.7,
        KP: 50e-6,
        LAMBDA: 0.02,
        GAMMA: 0.5,
        PHI: 0.8,
      },
      title: 'Edit MOSFET Model',
      onSave: (updatedJson) => {
        console.log('[Test] Saved MOSFET parameters:', updatedJson);
        const { modelName, type, level, VTO, KP, LAMBDA, GAMMA, PHI } = updatedJson;
        const paramStr = `VTO=${VTO} KP=${KP} LAMBDA=${LAMBDA} GAMMA=${GAMMA} PHI=${PHI}`;
        const modelLine = `.model ${modelName} ${type}(LEVEL=${level} ${paramStr})`;
        console.log('Generated SPICE line:', modelLine);
        alert('Generated SPICE line:\n' + modelLine);
      },
    });
  },

  /**
   * Test: Open resistor properties editor
   */
  openResistorEditor() {
    if (typeof window.openJsonEditor !== 'function') {
      console.error('JSON Editor not available. Make sure LabLayout is mounted.');
      return;
    }

    window.openJsonEditor({
      schema: {
        type: 'object',
        title: 'Resistor Properties',
        properties: {
          resistance: {
            type: 'number',
            title: 'Resistance (Ω)',
            description: 'The resistance value in ohms',
            default: 1000,
            minimum: 0.001,
            maximum: 10000000,
          },
          tolerance: {
            type: 'number',
            title: 'Tolerance (%)',
            description: 'Component tolerance percentage',
            default: 5,
            minimum: 0,
            maximum: 50,
          },
          power_rating: {
            type: 'number',
            title: 'Power Rating (W)',
            description: 'Maximum power dissipation',
            default: 0.25,
            enum: [0.125, 0.25, 0.5, 1, 2, 5, 10],
            enumNames: ['1/8 W', '1/4 W', '1/2 W', '1 W', '2 W', '5 W', '10 W'],
          },
          temperature_coefficient: {
            type: 'number',
            title: 'Temp Coefficient (ppm/°C)',
            description: 'Temperature coefficient',
            default: 100,
            minimum: -5000,
            maximum: 5000,
          },
        },
        required: ['resistance'],
      },
      uiSchema: {
        type: 'VerticalLayout',
        elements: [
          { type: 'Control', scope: '#/properties/resistance' },
          { type: 'Control', scope: '#/properties/tolerance' },
          { type: 'Control', scope: '#/properties/power_rating' },
          { type: 'Control', scope: '#/properties/temperature_coefficient' },
        ],
      },
      data: {
        resistance: 1000,
        tolerance: 5,
        power_rating: 0.25,
        temperature_coefficient: 100,
      },
      title: 'Edit Resistor Properties',
      onSave: (result) => {
        console.log('[Test] Saved resistor properties:', result);
        alert('Saved resistor properties:\n' + JSON.stringify(result, null, 2));
      },
    });
  },

  /**
   * Test: Open simple text fields editor
   */
  openSimpleEditor() {
    if (typeof window.openJsonEditor !== 'function') {
      console.error('JSON Editor not available. Make sure LabLayout is mounted.');
      return;
    }

    window.openJsonEditor({
      schema: {
        type: 'object',
        properties: {
          name: {
            type: 'string',
            title: 'Component Name',
            default: 'R1',
          },
          value: {
            type: 'number',
            title: 'Value',
            default: 100,
          },
          enabled: {
            type: 'boolean',
            title: 'Enabled',
            default: true,
          },
          notes: {
            type: 'string',
            title: 'Notes',
            description: 'Additional notes about this component',
          },
        },
      },
      uiSchema: {
        type: 'VerticalLayout',
        elements: [
          { type: 'Control', scope: '#/properties/name' },
          { type: 'Control', scope: '#/properties/value' },
          { type: 'Control', scope: '#/properties/enabled' },
          { type: 'Control', scope: '#/properties/notes' },
        ],
      },
      data: {
        name: 'Test Component',
        value: 100,
        enabled: true,
        notes: '',
      },
      title: 'Simple Editor Test',
      onSave: (result) => {
        console.log('[Test] Saved simple data:', result);
        alert('Saved:\n' + JSON.stringify(result, null, 2));
      },
    });
  },

  /**
   * Test: Open editor with horizontal layout
   */
  openHorizontalEditor() {
    if (typeof window.openJsonEditor !== 'function') {
      console.error('JSON Editor not available. Make sure LabLayout is mounted.');
      return;
    }

    window.openJsonEditor({
      schema: {
        type: 'object',
        properties: {
          firstName: { type: 'string', title: 'First Name', default: 'John' },
          lastName: { type: 'string', title: 'Last Name', default: 'Doe' },
          age: { type: 'integer', title: 'Age', default: 30, minimum: 0, maximum: 150 },
          email: { type: 'string', title: 'Email', default: 'john@example.com' },
        },
      },
      uiSchema: {
        type: 'VerticalLayout',
        elements: [
          {
            type: 'HorizontalLayout',
            elements: [
              { type: 'Control', scope: '#/properties/firstName' },
              { type: 'Control', scope: '#/properties/lastName' },
            ],
          },
          {
            type: 'HorizontalLayout',
            elements: [
              { type: 'Control', scope: '#/properties/age' },
              { type: 'Control', scope: '#/properties/email' },
            ],
          },
        ],
      },
      data: {
        firstName: 'John',
        lastName: 'Doe',
        age: 30,
        email: 'john@example.com',
      },
      title: 'Horizontal Layout Test',
      onSave: (result) => {
        console.log('[Test] Saved data:', result);
        alert('Saved:\n' + JSON.stringify(result, null, 2));
      },
    });
  },

  /**
   * Test: Open editor with your exact nested object structure
   */
  openNestedMOSFETEditor() {
    if (typeof window.openJsonEditor !== 'function') {
      console.error('JSON Editor not available. Make sure LabLayout is mounted.');
      return;
    }

    window.openJsonEditor({
      schema: {
        type: 'object',
        title: 'Level-1 MOSFET Model',
        properties: {
          modelName: {
            type: 'string',
            title: 'Model name',
            pattern: '^[a-zA-Z][a-zA-Z0-9_]*$',
          },
          type: {
            type: 'string',
            title: 'Type',
            enum: ['NMOS', 'PMOS'],
          },
          level: {
            type: 'integer',
            title: 'Level',
            const: 1,
          },
          parameters: {
            type: 'object',
            title: 'Parameters',
            properties: {
              VTO: {
                type: 'number',
                title: 'Threshold voltage (V)',
                minimum: -5,
                maximum: 5,
                default: 0.7,
              },
              KP: {
                type: 'number',
                title: 'KP (μA/V²)',
                minimum: 1e-6,
                default: 50e-6,
              },
              LAMBDA: {
                type: 'number',
                title: 'Lambda (1/V)',
                minimum: 0,
                maximum: 1,
                default: 0.02,
              },
              GAMMA: {
                type: 'number',
                title: 'Gamma (body effect)',
                minimum: 0,
                default: 0.5,
              },
              PHI: {
                type: 'number',
                title: 'Phi (V)',
                minimum: 0,
                default: 0.8,
              },
            },
            required: ['VTO'],
          },
        },
        required: ['modelName', 'type', 'level', 'parameters'],
      },
      // FLATTENED schema for better UI - parameters become top-level fields
      // This is the recommended approach for JSONForms vanilla renderers
      schema: {
        type: 'object',
        title: 'Level-1 MOSFET Model',
        properties: {
          modelName: {
            type: 'string',
            title: 'Model name',
            pattern: '^[a-zA-Z][a-zA-Z0-9_]*$',
          },
          type: {
            type: 'string',
            title: 'Type',
            enum: ['NMOS', 'PMOS'],
          },
          level: {
            type: 'integer',
            title: 'Level',
            enum: [1, 2, 3],
            default: 1,
          },
        },
        required: ['modelName', 'type'],
      },
      uiSchema: {
        type: 'VerticalLayout',
        elements: [
          { type: 'Control', scope: '#/properties/modelName' },
          { type: 'Control', scope: '#/properties/type' },
          { type: 'Control', scope: '#/properties/level' },
        ],
      },
      data: {
        modelName: 'nmos1',
        type: 'NMOS',
        level: 1,
      },
      title: 'Edit MOSFET Model',
      onSave: (updatedJson) => {
        console.log('[Test] Saved MOSFET parameters:', updatedJson);
        alert('Saved:\n' + JSON.stringify(updatedJson, null, 2));
      },
    });
  },
};

// Export test utilities to window for console access
if (typeof window !== 'undefined') {
  window.testJsonEditor = testJsonEditor;
  console.log('[JsonFormsEditor] Test utilities loaded. Use window.testJsonEditor to test.');
  console.log('[JsonFormsEditor] Available tests:');
  console.log('  - openMOSFETEditor()   : Flat structure MOSFET editor (recommended)');
  console.log('  - openResistorEditor() : Resistor properties editor');
  console.log('  - openSimpleEditor()   : Simple fields editor');
  console.log('  - openHorizontalEditor(): Horizontal layout test');
}

export default testJsonEditor;
